import React, { useEffect, useRef, useState } from 'react'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
// import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import mapboxgl, { LngLat, Marker } from 'mapbox-gl'
import styles from './mapBox.m.scss'

const isMobile = window.innerWidth < 768
const publisherDomain = parseHostname().publicationDomain
const center =
  publisherDomain === '24heures.ch'
    ? new LngLat(6.627086027591877, 46.52656029698641)
    : new LngLat(6.147621536695382, 46.19045481081329)
const zoom =
  publisherDomain === '24heures.ch' ? (isMobile ? 11 : 12) : isMobile ? 11 : 12
mapboxgl.accessToken =
  'pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJja292ZWk5ZnIwN2w0MndwZWQ0Nmpqcm1iIn0.d1OaviBwQQGO1_roYeaimQ'

export const MapContext = React.createContext<mapboxgl.Map | null>(null)

export const MapBox = ({
  markers,
  activeMarker,
  handleClickMarker,
  locked,
  handleMapLoaded
}: {
  markers: Marker[]
  activeMarker: Marker | null
  handleClickMarker: (marker: Marker) => void
  locked: boolean
  handleMapLoaded: (map) => void
}) => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const drawMarkersTimeout = useRef(null)
  const [mapboxMarkers, setMapboxMarkers] = useState<mapboxgl.Marker[]>([])
  const map = useRef(null)

  // Used to remove all the markers on the map and draw them again according
  // to the filters
  const drawMarkers = () => {
    // If the map is loaded and there are markers
    if (map && map.current) {
      // We will store the fresh markers here before setting them to the state
      const mapboxMarkersList = []

      // Erase all the markers
      mapboxMarkers.map(marker => marker.marker.remove())

      // Draw only the filtered markers if at least one filter is active
      for (const marker of markers) {
        // Create a DOM element for each marker
        const el = document.createElement('div')
        const width = 35
        const height = 35

        el.className = 'marker'
        el.style.backgroundImage = `url(${require('./resto-icon.svg')}`
        el.style.backgroundColor = '#ffffff'
        el.style.borderRadius = '50%'
        el.style.backgroundRepeat = 'no-repeat'
        el.style.boxShadow = '0px 0px 4px rgba(0, 0, 0, 0.6)'
        el.style.width = `${width}px`
        el.style.height = `${height}px`
        el.style.backgroundSize = '60%'
        el.style.backgroundPosition = 'center'
        el.style.cursor = 'pointer'

        if (activeMarker && marker.markerId === activeMarker.markerId) {
          el.className = 'marker active'
        }

        // Add click event to the marker
        el.addEventListener('click', e => {
          handleClickMarker(marker)
        })

        // Add the marker to the list of markers
        mapboxMarkersList.push({
          data: marker,
          marker: new mapboxgl.Marker(el)
            .setLngLat([marker.longitude, marker.latitude])
            .addTo(map.current)
        })
      }

      // Set the state of the markers
      setMapboxMarkers(mapboxMarkersList)
    } else {
      // If the map is not loaded yet, we will try again in a few seconds
      if (drawMarkersTimeout.current) {
        clearTimeout(drawMarkersTimeout.current)
      } else {
        drawMarkersTimeout.current = setTimeout(drawMarkers, 1000)
      }
    }
  }

  useEffect(() => {
    if (map.current) return // initialize map only once

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/ta-interaktiv/cl0cgrlms001e14rtvd7sxxq7',
      zoom: zoom,
      maxZoom: 17,
      minZoom: isMobile ? 3 : 7,
      center: center,
      attributionControl: false
    })

    map.current.on('load', () => {
      handleMapLoaded(map.current)
    })
  }, [])

  useEffect(() => {
    if (map && map.current && activeMarker) {
      map.current.flyTo({
        center: [activeMarker.longitude, activeMarker.latitude]
      })

      // Remove all the active classes on the markers
      mapboxMarkers.forEach(marker => {
        marker.marker.getElement().classList.remove('active')
      })

      // Show the selected marker on the map
      const mapboxMarker = mapboxMarkers.find(
        marker => marker.data.markerId === activeMarker.markerId
      )

      if (mapboxMarker) {
        mapboxMarker.marker.getElement().classList.add('active')
      }
    }
  }, [activeMarker])

  useEffect(() => {
    drawMarkers()
  }, [markers])

  useEffect(() => {
    if (map && map.current && locked) {
      map.current.boxZoom.disable()
      map.current.scrollZoom.disable()
      map.current.dragRotate.disable()
      map.current.dragPan.disable()
      map.current.keyboard.disable()
      map.current.doubleClickZoom.disable()
      map.current.touchZoomRotate.disable()
      map.current.active = false
    } else {
      map.current.boxZoom.enable()
      map.current.scrollZoom.enable()
      map.current.dragRotate.enable()
      map.current.dragPan.enable()
      map.current.keyboard.enable()
      map.current.doubleClickZoom.enable()
      map.current.touchZoomRotate.enable()
      map.current.active = true
    }
  }, [locked])

  return <div ref={mapContainer} className={styles.mapContainer} />
}
